import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import { url } from "../../../Config/Config";
import { CmsPageCreateAction } from "../../../Redux/Actions/CmsAction";
import CmsApiErrorAlert from "../shared/CmsApiErrorAlert";
import {
  validatePageTitle,
  validateSeo,
} from "../utils/cmsValidation";
import type { BodyJson, SeoJson } from "../../../types/cms";

const CmsCreatePagePage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [useNanoid, setUseNanoid] = useState(true);
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<{
    message?: string;
    errors?: string[];
  } | null>(null);

  const defaultBody: BodyJson = { schemaVersion: 1, sections: [] };
  const defaultSeo = (t: string): SeoJson => ({
    title: `${t.trim() || "Untitled"} | Skillbrew.AI`,
  });

  const handleSubmit = async () => {
    setClientError(null);
    setServerError(null);
    const te = validatePageTitle(title);
    if (te) {
      setClientError(te);
      return;
    }
    const seo = defaultSeo(title);
    const se = validateSeo(seo);
    if (se) {
      setClientError(se);
      return;
    }

    setLoading(true);
    const payload: Record<string, unknown> = {
      title: title.trim(),
      use_nanoid: useNanoid,
      body_json: defaultBody,
      seo_json: seo,
    };
    if (slug.trim()) {
      payload.slug = slug.trim();
    }

    const data = await dispatch(CmsPageCreateAction(payload));
    setLoading(false);

    if (data && data.status === true && data.data?.slug) {
      navigate(`${url}/cms/pages/${data.data.slug}/edit`);
    } else if (data && data.status === false) {
      setServerError({
        message: data.message,
        errors: data.errors,
      });
    }
  };

  return (
    <>
      {loading && <Spinner />}
      <div className="page-wrapper card">
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Create content page"
            breadcrumbs={[
              { label: "Content pages", path: `${url}/cms/pages`, isActive: false },
              { label: "New page", path: `${url}/cms/pages/new`, isActive: true },
            ]}
          />
        </div>
        <Box sx={{ m: 2, maxWidth: 560 }}>
          <Typography variant="h5" gutterBottom>
            New page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Slug is optional. Leave blank to auto-generate
            {useNanoid ? " with a unique suffix." : " from the title only (fails if taken)."}
          </Typography>

          <CmsApiErrorAlert
            message={clientError || serverError?.message}
            errors={serverError?.errors}
          />

          <TextField
            fullWidth
            required
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="Slug (optional)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            margin="normal"
            helperText="If set, must be unique. Cannot be changed after create."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={useNanoid}
                onChange={(_, c) => setUseNanoid(c)}
                disabled={!!slug.trim()}
              />
            }
            label="Append unique id to auto-slug (recommended)"
          />

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              Create draft
            </Button>
            <Button sx={{ ml: 1 }} onClick={() => navigate(`${url}/cms/pages`)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </div>
    </>
  );
};

export default CmsCreatePagePage;
